import { inject, Inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  expand,
  Observable,
  of,
  reduce,
  ReplaySubject,
  switchMap,
  takeWhile,
} from 'rxjs';
import { catchError, first, map, skipWhile } from 'rxjs/operators';
import { ApiService } from '../api-service/api.service';
import { SalespersonServiceInterface } from '../sales-orders/interfaces/salesperson-service-interface';
import { Salesperson } from './salesperson';
import { PartnerCRMSalespersonMarketService } from '../crm/salesperson-market.service';
import { AppConfigProvider, AppConfigService } from '../app-config.service';
import { SalespersonService as SalesPeopleService, Salesperson as SalesPeople } from '@vendasta/sales';
import { PagedResponse } from '@vendasta/sales/lib/salesperson/salesperson.service';

@Injectable({ providedIn: 'root' })
export class SalespersonService implements SalespersonServiceInterface {
  // TODO: This should be a Subject or a ReplaySubject with no value, initially.
  // Subscribers currently see "there are no salespeople" initially, when the
  // reality is: it's unknown how many salespeople there are until we load them.
  salespeopleSubject: BehaviorSubject<Salesperson[]> = new BehaviorSubject<Salesperson[]>([]);
  private salespeopleNoInitialValue$: Observable<Salesperson[]> = this.salespeopleSubject.pipe(
    skipWhile((v) => Array.isArray(v) && v.length === 0),
  );
  latestSalespeople$$: ReplaySubject<Salesperson[]> = new ReplaySubject(1);
  hasLoadedSalespeople$$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  salespeopleService = inject(SalesPeopleService);

  constructor(
    private apiService: ApiService,
    private readonly marketsService: PartnerCRMSalespersonMarketService,
    @Inject(AppConfigService) private appConfig: AppConfigProvider,
  ) {}

  readonly salespeople$: Observable<Salesperson[] | null> = this.salespeopleNoInitialValue$;

  /**
   * @deprecated Use salespeople$
   */
  get salespeople(): Observable<Salesperson[]> {
    return this.salespeopleSubject.asObservable();
  }

  get latestSalespeople$(): Observable<Salesperson[]> {
    return this.latestSalespeople$$.asObservable();
  }

  get hasLoadedSalespeople$(): Observable<boolean> {
    return this.hasLoadedSalespeople$$.asObservable();
  }

  loadAllSalespeopleAjax(): Observable<Salesperson[]> {
    return this.apiService
      .get('/_ajax/v1/salesperson/list')
      .pipe(map((response) => response.map((salesperson) => Salesperson.fromSalesPersonVObject(salesperson))));
  }

  private fetchSalesPeople(cursor: string, pageSize: number): Observable<PagedResponse<SalesPeople>> {
    return combineLatest([this.marketsService.currentMarketId$, this.appConfig.config$]).pipe(
      switchMap(([currentMarketId, cfg]) => {
        return this.salespeopleService.listSalespersons(
          { partnerId: cfg.partnerId, marketId: currentMarketId },
          cursor,
          pageSize,
        );
      }),
    );
  }

  private fetchAllSalesPeople(): Observable<Salesperson[]> {
    return this.fetchSalesPeople('', 500).pipe(
      expand((response) => this.fetchSalesPeople(response.nextCursor, 500)),
      takeWhile((response) => !!response.nextCursor, true),
      reduce((all, { results }) => all.concat(results), []),
      map((salesPeople) => this.convertToSalesperson(salesPeople)),
      catchError((error) => {
        console.error(error);
        return of([]);
      }),
    );
  }

  private convertToSalesperson(salesPeople: SalesPeople[]): Salesperson[] {
    return salesPeople.map(
      (salesPeople) =>
        new Salesperson({
          salesPersonId: salesPeople.salespersonId,
          firstName: salesPeople.firstName,
          lastName: salesPeople.lastName,
          email: salesPeople.email,
          country: salesPeople.countryCode,
          jobTitle: salesPeople.jobTitle,
          marketId: salesPeople.marketId,
          phoneNumber: salesPeople.phoneNumber,
          photoUrl: salesPeople.photoUrlHttps,
        }),
    );
  }

  loadAllSalespeople(): void {
    this.fetchAllSalesPeople().subscribe((salespeople) => {
      this.hasLoadedSalespeople$$.next(true);
      this.salespeopleSubject.next(salespeople);
      this.latestSalespeople$$.next(salespeople);
    });
  }

  public loadAllSalespeopleIfNotLoaded(): void {
    this.hasLoadedSalespeople$$.pipe(first()).subscribe((hasLoaded) => {
      if (hasLoaded) {
        return;
      }
      this.loadAllSalespeople();
    });
  }

  updateSalespeople(salespeople: Salesperson[]): void {
    this.salespeopleSubject.next(salespeople);
    this.latestSalespeople$$.next(salespeople);
  }

  /**
   * @deprecated Use getSalesperson and allow the service to manage it's own
   * optimizations.
   *
   * Gets a salesperson from the list of "all salespeople" if that has been
   * loaded.  Otherwise, loads all salespeople first.
   */
  getSalespersonFromCache(salespersonId: string): Salesperson {
    if (this.salespeopleSubject.getValue().length === 0) {
      this.loadAllSalespeople();
    }
    return this.salespeopleSubject.getValue().find((salesperson) => salesperson.salesPersonId === salespersonId);
  }

  getSalesperson(salespersonId: string, partnerId: string): Observable<Salesperson> {
    const url = `/_ajax/v1/get-salesperson/?salespersonId=${salespersonId}&partnerId=${partnerId}`;

    return this.apiService.get(url).pipe(
      map((response) => {
        if (!response) {
          return null;
        }
        return response.salesPersonId ? new Salesperson(response) : null;
      }),
    );
  }

  getSalespeople(salespersonIds: string[], partnerId: string): Observable<Salesperson[]> {
    const url = `/_ajax/v1/get-salespeople/`;
    const options = {
      params: {
        salespersonIds: salespersonIds,
        partnerId: partnerId,
      },
    };
    return this.apiService.get(url, options).pipe(
      map((response) => {
        if (!response) {
          return null;
        }
        const salesPeople: Salesperson[] = [];
        for (const salesPerson of response) {
          if (salesPerson?.salesPersonId) {
            salesPeople.push(new Salesperson(salesPerson));
          }
        }
        return salesPeople;
      }),
      catchError((err) => {
        console.error(err);
        return EMPTY;
      }),
    );
  }

  public getSortedSalespeople(): Observable<Salesperson[]> {
    return this.latestSalespeople$.pipe(
      map((salespeople) => {
        salespeople.sort((a, b) => {
          const aName = a.name.toLowerCase(),
            bName = b.name.toLowerCase();

          return aName < bName ? -1 : 1;
        });
        return salespeople;
      }),
    );
  }
}
